/**
 * Directory Modal Handler
 * Handles the complete stylist directory modal with search and pagination
 */

;(() => {
  'use strict'

  const ITEMS_PER_PAGE = 50
  const SEARCH_DEBOUNCE_MS = 300

  class DirectoryModal {
    constructor(container) {
      this.container = container
      this.apiBaseDomain = container.dataset.apiEndpoint || ''
      this.modal = container.querySelector('[data-element="directory-modal"]')
      this.openButtons = container.querySelectorAll('[data-element="directory-modal-open"]')
      this.closeButton = container.querySelector('[data-element="directory-modal-close"]')
      this.closeButtonDesktop = container.querySelector('[data-element="directory-modal-close-desktop"]')
      this.searchInput = container.querySelector('[data-element="directory-search-input"]')
      this.listContainer = container.querySelector('[data-element="directory-list"]')
      this.loadingElement = container.querySelector('[data-element="directory-loading"]')
      this.emptyElement = container.querySelector('[data-element="directory-empty"]')
      this.paginationContainer = container.querySelector('[data-element="directory-pagination"]')
      this.prevButton = container.querySelector('[data-element="directory-prev"]')
      this.nextButton = container.querySelector('[data-element="directory-next"]')
      this.pageInfo = container.querySelector('[data-element="directory-page-info"]')

      this.state = {
        isOpen: false,
        isLoading: false,
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        searchQuery: '',
        stylists: [],
        scrollY: 0,
      }

      this.searchTimeout = null
      this.boundKeydown = null
      this.abortController = null

      this.init()
    }

    init() {
      if (!this.modal) return
      this.bindEvents()
    }

    bindEvents() {
      // Open modal
      this.openButtons.forEach((button) => {
        button.addEventListener('click', () => this.open())
      })

      // Close modal via buttons
      ;[this.closeButton, this.closeButtonDesktop].forEach((target) => {
        if (!target) return
        target.addEventListener('click', () => this.close())
      })

      // Click outside to close (on the modal backdrop)
      this.modal?.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.close()
        }
      })

      // Search input
      if (this.searchInput) {
        this.searchInput.addEventListener('input', (e) => {
          this.handleSearch(e.target.value)
        })
        
        // Also handle Enter key for immediate search
        this.searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            // Clear debounce and search immediately
            if (this.searchTimeout) {
              clearTimeout(this.searchTimeout)
            }
            const query = e.target.value || ''
            this.state.searchQuery = query.trim()
            this.state.currentPage = 1
            this.fetchStylists(1, query.trim())
          }
        })
      }

      // Pagination buttons
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => {
          if (this.state.currentPage > 1) {
            this.goToPage(this.state.currentPage - 1)
          }
        })
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => {
          if (this.state.currentPage < this.state.totalPages) {
            this.goToPage(this.state.currentPage + 1)
          }
        })
      }

      // Escape key to close
      this.boundKeydown = (e) => {
        if (e.key === 'Escape' && this.state.isOpen) {
          this.close()
        }
      }
      document.addEventListener('keydown', this.boundKeydown)
    }

    buildApiUrl(page = 1, search = '') {
      if (!this.apiBaseDomain) {
        console.warn('DirectoryModal: apiBaseDomain is empty')
        return null
      }
      
      try {
        const base = this.apiBaseDomain.replace(/\/$/, '')
        const url = new URL(`${base}/api/stylists`)
        url.searchParams.set('page', page.toString())
        url.searchParams.set('limit', ITEMS_PER_PAGE.toString())
        if (search && search.trim()) {
          url.searchParams.set('search', search.trim())
        }
        return url.toString()
      } catch (error) {
        // Fallback: construct URL string manually if URL constructor fails
        console.warn('DirectoryModal: URL constructor failed, using fallback', error)
        const base = this.apiBaseDomain.replace(/\/$/, '')
        let urlStr = `${base}/api/stylists?page=${page}&limit=${ITEMS_PER_PAGE}`
        if (search && search.trim()) {
          urlStr += `&search=${encodeURIComponent(search.trim())}`
        }
        return urlStr
      }
    }

    async fetchStylists(page = 1, search = '') {
      const url = this.buildApiUrl(page, search)
      if (!url) {
        console.warn('DirectoryModal: API endpoint not configured')
        this.setLoading(false)
        return
      }

      // Cancel any pending request
      if (this.abortController) {
        this.abortController.abort()
      }
      this.abortController = new AbortController()

      this.setLoading(true)

      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
          },
          signal: this.abortController.signal,
        })

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`)
        }

        const data = await response.json()

        this.state.stylists = Array.isArray(data.stylists) ? data.stylists : []
        this.state.currentPage = data.pagination?.page || 1
        this.state.totalPages = data.pagination?.total_pages || 1
        this.state.totalCount = data.pagination?.total_count || 0

        this.render()
      } catch (error) {
        if (error.name === 'AbortError') {
          // Request was cancelled, ignore
          return
        }
        console.error('DirectoryModal: Failed to fetch stylists', error)
        this.state.stylists = []
        this.state.totalPages = 1
        this.state.totalCount = 0
        this.render()
      } finally {
        this.setLoading(false)
        this.abortController = null
      }
    }

    handleSearch(query) {
      // Clear any existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      const searchValue = query || ''

      // Debounce the search
      this.searchTimeout = setTimeout(() => {
        const trimmedQuery = searchValue.trim()
        this.state.searchQuery = trimmedQuery
        this.state.currentPage = 1 // Reset to first page on new search
        this.fetchStylists(1, trimmedQuery)
      }, SEARCH_DEBOUNCE_MS)
    }

    goToPage(page) {
      if (page < 1 || page > this.state.totalPages) return
      this.state.currentPage = page
      this.fetchStylists(page, this.state.searchQuery)

      // Scroll list to top
      if (this.listContainer) {
        this.listContainer.scrollTop = 0
      }
    }

    setLoading(isLoading) {
      this.state.isLoading = isLoading

      if (this.loadingElement) {
        this.loadingElement.style.display = isLoading ? 'flex' : 'none'
      }

      if (this.listContainer) {
        this.listContainer.style.opacity = isLoading ? '0.5' : '1'
      }
    }

    render() {
      this.renderList()
      this.renderPagination()
    }

    renderList() {
      if (!this.listContainer) return

      const { stylists } = this.state

      // Show empty state if no results
      if (this.emptyElement) {
        this.emptyElement.style.display = stylists.length === 0 && !this.state.isLoading ? 'flex' : 'none'
      }

      if (stylists.length === 0) {
        this.listContainer.innerHTML = ''
        return
      }

      const html = stylists.map((stylist) => this.renderStylistCard(stylist)).join('')
      this.listContainer.innerHTML = html
    }

    renderStylistCard(stylist) {
      const name = this.escapeHtml(stylist.name || 'Unknown Stylist')
      const role = this.escapeHtml(stylist.role || 'IBE® Stylist')
      const profilePicture = stylist.profile_picture || ''
      const initials = this.getInitials(stylist.name || '')

      const avatarContent = profilePicture
        ? `<img src="${this.escapeHtml(profilePicture)}" alt="${name}" loading="lazy">`
        : `<span class="stylist-locator__directory-card-initials">${initials}</span>`

      return `
        <div class="stylist-locator__directory-card">
          <div class="stylist-locator__directory-card-avatar">
            ${avatarContent}
          </div>
          <div class="stylist-locator__directory-card-info">
            <span class="stylist-locator__directory-card-role">${role}</span>
            <span class="stylist-locator__directory-card-name">${name}</span>
          </div>
        </div>
      `
    }

    renderPagination() {
      if (!this.paginationContainer) return

      const { currentPage, totalPages, totalCount } = this.state

      // Hide pagination if only one page or no results
      if (totalPages <= 1 || totalCount === 0) {
        this.paginationContainer.style.display = 'none'
        return
      }

      this.paginationContainer.style.display = 'flex'

      // Update page info
      if (this.pageInfo) {
        this.pageInfo.textContent = `Page ${currentPage} of ${totalPages}`
      }

      // Update button states
      if (this.prevButton) {
        this.prevButton.disabled = currentPage <= 1
      }

      if (this.nextButton) {
        this.nextButton.disabled = currentPage >= totalPages
      }
    }

    open() {
      if (this.state.isOpen) return

      this.state.isOpen = true
      this.lockBodyScroll()

      // Add open class with animation delay
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.modal?.classList.add('stylist-locator__directory-modal--open')
        })
      })

      document.body.classList.add('stylist-locator__directory-modal-open')

      // Reset state and fetch initial data
      this.state.currentPage = 1
      this.state.searchQuery = ''
      if (this.searchInput) {
        this.searchInput.value = ''
      }

      this.fetchStylists(1, '')

      // Focus the search input
      setTimeout(() => {
        this.searchInput?.focus()
      }, 100)
    }

    close() {
      if (!this.state.isOpen) return

      this.state.isOpen = false
      this.modal?.classList.remove('stylist-locator__directory-modal--open')
      document.body.classList.remove('stylist-locator__directory-modal-open')

      this.unlockBodyScroll()

      // Cancel any pending requests
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }

      // Clear search timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
        this.searchTimeout = null
      }

      // Return focus to the button that opened the modal
      if (this.openButtons.length > 0) {
        this.openButtons[0].focus()
      }
    }

    lockBodyScroll() {
      this.state.scrollY = window.scrollY || 0
      document.body.style.position = 'fixed'
      document.body.style.top = `-${this.state.scrollY}px`
      document.body.style.width = '100%'
    }

    unlockBodyScroll() {
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''

      if (this.state.scrollY !== undefined) {
        window.scrollTo(0, this.state.scrollY)
      }
    }

    getInitials(name) {
      if (!name) return ''
      const parts = name.trim().split(/\s+/)
      if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase()
      }
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
    }

    escapeHtml(text) {
      if (!text) return ''
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML
    }

    destroy() {
      if (this.boundKeydown) {
        document.removeEventListener('keydown', this.boundKeydown)
        this.boundKeydown = null
      }

      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
        this.searchTimeout = null
      }

      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }
    }
  }

  // Initialize when DOM is ready
  function initDirectoryModal() {
    const containers = document.querySelectorAll('.js-stylist-locator')
    containers.forEach((container) => {
      // Check if already initialized
      if (container._directoryModal) return
      container._directoryModal = new DirectoryModal(container)
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDirectoryModal)
  } else {
    initDirectoryModal()
  }

  // Handle Shopify section reload
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target?.querySelector('.js-stylist-locator')
    if (container && !container._directoryModal) {
      container._directoryModal = new DirectoryModal(container)
    }
  })

  document.addEventListener('shopify:section:unload', (event) => {
    const container = event.target?.querySelector('.js-stylist-locator')
    if (container && container._directoryModal) {
      container._directoryModal.destroy()
      container._directoryModal = null
    }
  })
})()
